import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../../redux/Actions/authActions";
import ForgotPassword from "../ForgotPassword/ForgotPassword";
import LanguageToggle from "../LanguageToggle/LanguageToggle";
import LoadingSpinner from "../Loader/LoadingSpinner";
import MessageCard from "../MessageCard/MessageCard";
import Meta from "../Meta/Meta";

const Login = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [validationErrors, setValidationErrors] = useState({});

  const validate = () => {
    const errors = {};
    if (!email) {
      errors.email = t("login.emailRequired", {
        defaultValue: "Email is required",
      });
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.email = t("login.emailInvalid", {
          defaultValue: "Please enter a valid email address",
        });
      }
    }
    if (!password) {
      errors.password = t("login.passwordRequired", {
        defaultValue: "Password is required",
      });
    }
    return errors;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const errors = validate();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setMessage(Object.values(errors).join("\n"));
      setMessageType("error");
      return;
    }
    setLoading(true);
    try {
      await dispatch(loginUser({ email, password }, navigate));
      setMessage(t("login.loginSuccess"));
      setMessageType("success");
    } catch (err) {
      const fallback = t("login.loginError");
      const error =
        err.response?.status === 401
          ? t("login.loginFailed")
          : err.response?.data?.message || err.message || fallback;
      setMessage(error);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Meta
        title="ALGermanist - Login"
        description="Sign in to your ALGermanist account to access your dashboard, courses, and learning progress."
        keywords="login, sign in, ALGermanist, student portal"
      />
      <Grid
        container
        sx={{
          height: "100vh",
        }}
      >
        {loading && <LoadingSpinner loading />}
        {message && (
          <MessageCard
            message={message}
            type={messageType}
            onClose={() => setMessage(null)}
          />
        )}

        {/* RIGHT SIDE (FORM) - NOW FIRST */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            gap: 5,
            p: 3,
          }}
        >
          <Container maxWidth="xs" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
            {/* Language switcher */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <LanguageToggle />
            </Box>
            {/* Mobile Top Logo */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                justifyContent: "flex-start",
                mt: 1,
                mb: 3,
              }}
            >
              <Box component={Link} to="/" sx={{ display: "inline-flex" }}>
                <Box
                  component="img"
                  src="/al-germanist-logo.png"
                  alt="ALGermanist"
                  sx={{ height: 52 }}
                />
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mt: { xs: 0, md: 12 },
                mb: 12,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("login.title")}
            </Typography>

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label={t("login.email")}
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 3,
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />

              <TextField
                fullWidth
                label={t("login.password")}
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 3,
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
                error={!!validationErrors.password}
                helperText={validationErrors.password}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              <Typography
                onClick={() => setOpenForgotPassword(true)}
                sx={{
                  color: "var(--primary-color)",
                  cursor: "pointer",
                  fontSize: 14,
                  mb: 3,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("login.forgot")}
              </Typography>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: "50px" }}
              >
                {t("login.loginNow")}
              </Button>

              <Typography sx={{ mt: 3, textAlign: "center", fontSize: 14 }}>
                {t("login.noAccount")}{" "}
                <Link
                  to="/signup"
                  style={{
                    color: "var(--primary-color)",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                >
                  {t("login.signup")}
                </Link>
              </Typography>
            </form>
          </Container>
        </Grid>

        {/* LEFT SIDE (HERO) - NOW SECOND */}
        <Grid
          item
          xs={false}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            position: "relative",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-start",
            overflow: "hidden",
            textAlign: "left",
            px: { md: 5, lg: 7 },
            py: { md: 5, lg: 6 },
            backgroundImage:
              "linear-gradient(180deg, rgba(8, 12, 13, 0.2) 28%, rgba(8, 12, 13, 0.78) 100%), url('/login-study-hero.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "58% center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{
              position: "relative",
              zIndex: 1,
              display: "inline-flex",
              cursor: "pointer",
              transform: "translateY(-20px)",
            }}
          >
            <Box
              component="img"
              src="/al-germanist-logo.png"
              alt="ALGermanist"
              sx={{
                height: { md: 88, lg: 108 },
                maxWidth: 360,
                objectFit: "contain",
                mixBlendMode: "multiply",
              }}
            />
          </Box>
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 600 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.3,
                color: "#fff",
                mb: 0,
                fontSize: { md: 30, lg: 38 },
                textShadow: "0 3px 18px rgba(0,0,0,0.5)",
              }}
            >
              {t("login.sloganLine1", { ns: "ui" })}{" "}
              <Box component="span" sx={{ color: "#FFD600" }}>
                {t("login.sloganLine2", { ns: "ui" })}
              </Box>{" "}
            </Typography>
          </Box>
        </Grid>

        <Dialog
          open={openForgotPassword}
          onClose={() => setOpenForgotPassword(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ direction: "rtl" }}>
            {t("login.forgotTitle")}
          </DialogTitle>
          <DialogContent sx={{ direction: "rtl" }}>
            <ForgotPassword onClose={() => setOpenForgotPassword(false)} />
          </DialogContent>
        </Dialog>
      </Grid>
    </>
  );
};

export default Login;
